// A purely numeric search should land on the matching token, the way an
// edition-code search lands on the edition ("6421" → The Currency #6421).
// token_id is a string in the schema while nft_number is an Int, which caps
// out at safe-integer range — hence the extra guard before comparing it.
const numericTokenMatches = (value: string) => {
	const trimmed = value.trim();

	if (!/^\d+$/.test(trimmed)) {
		return [];
	}

	return [
		{ token_id: { _eq: trimmed } },
		...(Number(trimmed) <= Number.MAX_SAFE_INTEGER
			? [{ nft_number: { _eq: Number(trimmed) } }]
			: []),
	];
};

export const getVariables = (value: string) => ({
	artistsFilter: {
		_or: [
			{ name: { _icontains: value } },
			{ search_terms: { _icontains: value } },
			// The artists credited on a drop's editions, as editions match by
			// their drop's name below.
			{
				editions: {
					Editions_id: {
						product: { drop_config: { name: { _icontains: value } } },
					},
				},
			},
		],
	},
	editionsFilter: {
		_or: [
			{ name: { _icontains: value } },
			{ code: { _icontains: value } },
			{ keywords: { _icontains: value } },
			{ artists: { Artists_id: { name: { _icontains: value } } } },
			{ artists: { Artists_id: { search_terms: { _icontains: value } } } },
			// A drop is often named for its theme rather than its pieces (the
			// "Daffodils" drop sells "Golden Meadow" and "Whispers of Spring"),
			// so searching the drop's name lists the editions it sells.
			{ product: { drop_config: { name: { _icontains: value } } } },
		],
	},
	seriesFilter: {
		_and: [
			{
				_or: [
					{ name: { _icontains: value } },
					{ code: { _icontains: value } },
					{ keywords: { _icontains: value } },
					{ artists: { Artists_id: { name: { _icontains: value } } } },
					{
						artists: {
							Artists_id: { search_terms: { _icontains: value } },
						},
					},
					// The series a drop's editions belong to, as editions match by
					// their drop's name below.
					{
						editions: {
							product: { drop_config: { name: { _icontains: value } } },
						},
					},
				],
			},
			{ parent_series: { _none: { Series_id: { id: { _nnull: true } } } } },
		],
	},
	primaryFilter: {
		_or: [
			{ artwork_title: { _icontains: value } },
			{ artwork_code: { _icontains: value } },
			{ primary_collection: { name: { _icontains: value } } },
			{ primary_collection: { code: { _icontains: value } } },
			{ artists: { Artists_id: { name: { _icontains: value } } } },
			{ artists: { Artists_id: { search_terms: { _icontains: value } } } },
		],
	},
	primaryCollectionsFilter: {
		_or: [
			{ name: { _icontains: value } },
			{ code: { _icontains: value } },
			{ artists: { Artists_id: { name: { _icontains: value } } } },
			{
				artists: {
					Artists_id: { search_terms: { _icontains: value } },
				},
			},
		],
	},
	booksFilter: {
		_or: [
			{ title: { _icontains: value } },
			{
				authors: {
					Publishing_Authors_And_Artists_id: {
						full_name: { _icontains: value },
					},
				},
			},
			{
				artists: {
					Publishing_Authors_And_Artists_id: {
						full_name: { _icontains: value },
					},
				},
			},
		],
	},
	merchFilter: {
		_and: [
			{
				_or: [
					{ title: { _icontains: value } },
					// Shop_Collection's display name lives on `title` (it has no `name`).
					{ shop_collection: { title: { _icontains: value } } },
					{
						shop_collection: {
							artists: { Artists_id: { name: { _icontains: value } } },
						},
					},
				],
			},
			// Exclude product variants (which carry a parent_item) so search only
			// surfaces top-level merch products, matching the dedicated merch search.
			{ parent_item: { id: { _null: true } } },
		],
	},
	shopCollectionsFilter: {
		_or: [
			{ title: { _icontains: value } },
			{ artist_override_text: { _icontains: value } },
			{ artists: { Artists_id: { name: { _icontains: value } } } },
			{ artists: { Artists_id: { search_terms: { _icontains: value } } } },
		],
	},
	talksFilter: {
		_or: [
			{ title: { _icontains: value } },
			{ short_description: { _icontains: value } },
		],
	},
	podcastsFilter: {
		_or: [
			{ title: { _icontains: value } },
			{ short_description: { _icontains: value } },
			{
				podcast_hosts: {
					HENI_Talks_Speakers_id: { name: { _icontains: value } },
				},
			},
		],
	},
	nftCollectionsFilter: {
		_or: [
			{ title: { _icontains: value } },
			{ artist: { name: { _icontains: value } } },
			{ artist: { search_terms: { _icontains: value } } },
		],
	},
	nftTokensFilter: {
		_and: [
			// Same visibility rule as the NFT gallery: hidden tokens never surface.
			{ show_in_gallery: { _eq: true } },
			{
				_or: [
					{ title: { _icontains: value } },
					// Matching on the collection makes a collection-name (or artist)
					// search list the collection's first tokens beneath its row, the
					// way a series search previews the series' editions.
					{ nft_collection: { title: { _icontains: value } } },
					{ nft_collection: { artist: { name: { _icontains: value } } } },
					{
						nft_collection: {
							artist: { search_terms: { _icontains: value } },
						},
					},
					...numericTokenMatches(value),
				],
			},
		],
	},
});
